<template>        
    <v-menu 
            v-model="isMenuOpen" 
            :offsetY="offsetY" 
            :offset-x="offsetX" 
            :nudge-right="offsetX?4:0" 
            :bottom="bottom" 
            :nudge-bottom="nudgeBottom" 
            :nudge-top="nudgeTop"             
            :closeOnContentClick="closeOnContentClick">
        <template v-slot:activator="{ on }">
            <v-btn v-on="on" :class="cls" class="px-4" 
                :loading="loading" 
                :block="block"  
                outlined
                :color="compColor" 
                @click="onClick">
                
                <span v-if="!dropRight" class="inline-flex icon">
                    <Icon class="btn-icon mr-2" type="dropdown"></Icon>
                </span>
                <span class="text"><slot>{{compText}}</slot></span>
                <span v-if="dropRight" class="inline-flex icon">
                    <Icon class="btn-icon ml-2" type="dropdown"></Icon>
                </span>
            </v-btn>
        </template>
        <slot name="dropmenu">
            <v-list dense>
                <slot name="dropdown" v-bind:fnClose="onClose"></slot>
            </v-list>
        </slot>
    </v-menu>
</template>

<script setup>
import Icon from '@shared/ui/controls/icons/Icon'
import action from '@app/action'
import {computed, ref} from 'vue'

const isMenuOpen = ref(false);
const emit = defineEmits(['click', 'preAction'])

// onClose is passed to the dropdown slot. 
// It is to be used when a menu item has a child menu. 
// In this case, clicking the child will not close the parent by default. So, the child must call close first. 
// It is the way it is. 
function onClose () {
    isMenuOpen.value = false;
}

//   // A sub button which acts as a dropdown button. 
//   // Since we do not want to restrict the dropdown itself, no action is specified.
//   import ActionMenuItem from "@controls/menu/ActionMenuItem.vue";
//   <ActionButton sub dropdown>
//      <template v-slot:dropdown>
//            <ActionMenuItem action="invoice.modify" no-execute  @click="onAddEmptyTextLine">Lege regel</ActionMenuItem> 
//            <ActionMenuItem action="invoice.modify" no-execute  @click="onAddStandardTextLine">Standaard tekst</ActionMenuItem> 
//      </template>
//      Tekst
//   </ActionButton>
// 

const props = defineProps({
    action: {
        type: [String],
        default: null
    },     
    main: {
        type: [Boolean],
        default: false
    },
    primary: {
        type: [Boolean],
        default: false
    },
    error: {
        type: [Boolean],
        default: false
    },
    success: {
        type: [Boolean],
        default: false
    },
    warning: {
        type: [Boolean],
        default: false
    },

    sub: {
        type: [Boolean],
        default: false
    },
    bottom: {
        type: [Boolean],
        default: false
    },
    dropdown: {
        type: [Boolean],
        default: false
    },
    dropRight: {
        type: [Boolean],
        default: false
    },        
    offsetX: {
        type: [Boolean],
        default: false
    },
    offsetY: {
        type: [Boolean],
        default: true
    },
    nudgeBottom: {
        type: [String, Number],
    },
    nudgeTop: {
        type: [String, Number],
    },
    closeOnContentClick: {
        type: [Boolean],
        default: true
    },        
    noText: {
        type: [Boolean],
        default: false
    },
    disabled: {
        type: [Boolean],
        default: false
    },
    // color and icon are optional. 
    // For 'standard' types, colors and icons are predefined and can be overruled by specifying color and / or icon property.
    color: {
        type: [String],
        default: ""
    },
    iconButtonColor: {
        type: [String],
        default: ""
    },        
    iconRight: {
        type: [Boolean],
        default: false
    },
    block: {
        type: [Boolean],
        default: false
    },
    loading: {
        type: [Boolean],
        default: false
    },
    ml: {
        type: [Boolean, String, Number],
        default: false
    },
    model: {
        type: [Object]
    },
})

const compDisabled = computed({
    get() {
        if (props.disabled) {
            return true;
        }
        if (props.model && props.model.disabled) {
            return true;
        }
        if (props.model && props.model.isDataLoading) {
            return true;
        }
        if (props.action) {
            return !action.can(props.action);
        }
        return false;
    }
})

const cls = computed({
    get() {
        let disabled = compDisabled.value ? "disabled":"";
        let main = props.main?"main":"";
        let primary = props.primary?"primary":"";
        let error = props.error?'error':"" 
        let success = props.success?'success':"" 
        let warning = props.warning?'warning':"" 
        let dd = props.dropdown?"dropdown":"";
        let sub = props.sub?"sub":"";
        if (props.sub && props.error) {
            error=""
            sub="sub-error";
        }
        let ml = (props.ml === true) ? "ml-2": (Number(props.ml) ? `ml-${Number(props.ml)}` : "");
        return `action-button ${main} ${primary} ${error} ${warning} ${success} ${dd} ${sub} ${disabled} ${ml}`;
    }
})
const compText = computed({
    get() {
        if (props.action) {
            return action.textFor(props.action);
        }
        return "";
    },
})
const compColor = computed({
    get() {
        return props.color
    }
})

async function onClick(evt) {
    if (props.compDisabled) {
        return; // Do nothing.
    }
    emit('preAction')
    emit('click', evt)
};

</script>
